import React, { useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AdminLayout } from '../../layouts/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import {
  useClients,
  useCreateClient,
  useUpdateClient,
  useDeleteClient,
  useDebounce,
} from '../../hooks';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { ClientForm } from '../../components/organisms/ClientForm';
import { Pagination } from '../../components/molecules/Pagination';
import { toast } from '../../utils/toast';
import { clientsService } from '../../services/clients.service';
import type { Client, CreateClientDto, UpdateClientDto } from '@lnr-v2/types';
import styles from './ClientsSimplePage.module.css';

interface SortableRowProps {
  client: Client;
  index: number;
  isAdmin: boolean;
  deletingId: string | null;
  onEdit: (client: Client) => void;
  onDelete: (clientId: string) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({
  client,
  index,
  isAdmin,
  deletingId,
  onEdit,
  onDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: client.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={isDragging ? styles.dragging : ''}
    >
      <td className={styles.dragHandle}>
        <button
          className={styles.dragButton}
          {...attributes}
          {...listeners}
          aria-label="Glisser pour réordonner"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4 4a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0 4a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0 4a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm5-8a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0 4a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0 4a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
          </svg>
        </button>
      </td>
      <td className={styles.orderColumn}>{index + 1}</td>
      <td>
        {client.logo ? (
          <img
            src={client.logo}
            alt={client.name}
            className={styles.thumbnail}
            loading="lazy"
          />
        ) : (
          <div className={styles.thumbnailPlaceholder}>
            {client.name.charAt(0).toUpperCase()}
          </div>
        )}
      </td>
      <td>
        <div className={styles.clientDetails}>
          <button className={styles.clientName} onClick={() => onEdit(client)}>
            {client.name}
          </button>
          {client.description && (
            <span className={styles.clientDescription}>
              {client.description}
            </span>
          )}
        </div>
      </td>
      <td>
        <span
          className={`${styles.statusBadge} ${
            client.isActive ? styles.statusActive : styles.statusInactive
          }`}
        >
          {client.isActive ? 'Actif' : 'Inactif'}
        </span>
      </td>
      <td className={styles.centerAlign}>
        <span className={styles.caseCount}>{client.projectCount || 0}</span>
      </td>
      {isAdmin && (
        <td>
          <div className={styles.actions}>
            <button
              onClick={() => onEdit(client)}
              className={styles.editButton}
              title="Modifier"
            >
              Modifier
            </button>
            <button
              onClick={() => onDelete(client.id)}
              disabled={deletingId === client.id}
              className={styles.deleteButton}
              title="Supprimer"
            >
              {deletingId === client.id ? '...' : 'Supprimer'}
            </button>
          </div>
        </td>
      )}
    </tr>
  );
};

export const ClientsSimplePage: React.FC = () => {
  const { user } = useAuth();
  const { data, loading, error, refetch, updateFilter } = useClients();
  const { createClient, loading: creating } = useCreateClient();
  const { updateClient, loading: updating } = useUpdateClient();
  const { deleteClient } = useDeleteClient();

  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);

  // Local state for drag-and-drop reordering
  const [localClients, setLocalClients] = useState<Client[]>([]);
  const originalOrderRef = useRef<Client[] | null>(null);
  const [reorderError, setReorderError] = useState(false);

  // Sync local state with data from API
  useEffect(() => {
    if (data?.items) {
      setLocalClients(data.items);
    }
  }, [data?.items]);

  // Rollback on reorder error
  useEffect(() => {
    if (reorderError && originalOrderRef.current) {
      setLocalClients(originalOrderRef.current);
      originalOrderRef.current = null;
      setReorderError(false);
    }
  }, [reorderError]);

  // Reorder mutation
  const reorderMutation = useMutation({
    mutationFn: (updates: Array<{ id: string; sortOrder: number }>) =>
      clientsService.reorder(updates),
    onSuccess: () => {
      toast.success('Ordre des clients mis à jour');
      originalOrderRef.current = null;
      refetch();
    },
    onError: () => {
      toast.error('Échec de la réorganisation. Ordre restauré.');
      setReorderError(true);
    },
  });

  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (_event: DragStartEvent) => {
    originalOrderRef.current = [...localClients];
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localClients.findIndex((c) => c.id === active.id);
      const newIndex = localClients.findIndex((c) => c.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newClients = arrayMove(localClients, oldIndex, newIndex);
        setLocalClients(newClients);

        const updates = newClients.map((c, index) => ({
          id: c.id,
          sortOrder: index,
        }));
        reorderMutation.mutate(updates);
      }
    } else {
      originalOrderRef.current = null;
    }
  };

  // Update filter when debounced search term changes
  useEffect(() => {
    updateFilter('search', debouncedSearch);
  }, [debouncedSearch, updateFilter]);

  const isAdmin =
    user?.role === 'SUPER_ADMIN' ||
    user?.role === 'ADMIN' ||
    user?.role === 'admin';

  const handleOpenCreate = () => {
    setEditingClient(null);
    setShowModal(true);
  };

  const handleOpenEdit = (client: Client) => {
    setEditingClient(client);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingClient(null);
  };

  const handleSubmit = async (data: CreateClientDto | UpdateClientDto) => {
    try {
      if (editingClient) {
        await updateClient(editingClient.id, data);
        toast.success('Client mis à jour avec succès');
      } else {
        await createClient(data as CreateClientDto);
        toast.success('Client créé avec succès');
      }
      handleCloseModal();
      await refetch();
    } catch (error) {
      console.error('Failed to save client:', error);
      toast.error(
        editingClient
          ? 'Échec de la mise à jour du client'
          : 'Échec de la création du client'
      );
      throw error;
    }
  };

  const handleDelete = async (clientId: string) => {
    if (
      !window.confirm(
        'Êtes-vous sûr de vouloir supprimer ce client ? Cette action est irréversible.'
      )
    ) {
      return;
    }

    setDeletingId(clientId);
    try {
      await deleteClient(clientId);
      toast.success('Client supprimé avec succès');
      await refetch();
    } catch (error) {
      console.error('Failed to delete client:', error);
      toast.error('Échec de la suppression du client');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading && !data) {
    return (
      <AdminLayout>
        <LoadingOverlay isLoading={true} />
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className={styles.container}>
          <div className={styles.error}>
            <h3 className={styles.errorTitle}>Erreur de chargement</h3>
            <p className={styles.errorMessage}>{error.message}</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.title}>Clients</h1>
          {isAdmin && (
            <button onClick={handleOpenCreate} className={styles.addButton}>
              + Nouveau Client
            </button>
          )}
        </div>

        {/* Filter Bar */}
        <div className={styles.filterBar}>
          <div className={styles.searchWrapper}>
            <svg
              className={styles.searchIcon}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Rechercher un client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>

        {/* Create/Edit Client Modal */}
        {showModal && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalContainer}>
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>
                  {editingClient ? 'Modifier le Client' : 'Nouveau Client'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className={styles.modalCloseButton}
                  aria-label="Fermer"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className={styles.modalContent}>
                <ClientForm
                  initialData={editingClient || undefined}
                  onSubmit={handleSubmit}
                  onCancel={handleCloseModal}
                  isSubmitting={creating || updating}
                />
              </div>
            </div>
          </div>
        )}

        {/* Clients Table */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.dragColumn}></th>
                  <th className={styles.orderColumn}>#</th>
                  <th className={styles.imageColumn}>Logo</th>
                  <th>Client</th>
                  <th>Status</th>
                  <th className={styles.centerAlign}>Cases</th>
                  {isAdmin && <th className={styles.actionsColumn}>Actions</th>}
                </tr>
              </thead>
              <SortableContext
                items={localClients.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                <tbody>
                  {localClients.length > 0 ? (
                    localClients.map((client, index) => (
                      <SortableRow
                        key={client.id}
                        client={client}
                        index={index}
                        isAdmin={isAdmin}
                        deletingId={deletingId}
                        onEdit={handleOpenEdit}
                        onDelete={handleDelete}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan={isAdmin ? 7 : 6}>
                        <div className={styles.empty}>
                          <div className={styles.emptyIcon}>👤</div>
                          <h3>Aucun client</h3>
                          <p>Commencez par créer votre premier client</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </SortableContext>
            </table>
          </div>
        </DndContext>

        {/* Pagination */}
        {data && data.pagination && data.pagination.total > 0 && (
          <Pagination
            currentPage={data.pagination.page}
            totalPages={data.pagination.totalPages}
            total={data.pagination.total}
            pageSize={data.pagination.limit}
            onPageChange={(page) => updateFilter('page', page)}
            onPageSizeChange={(limit) => updateFilter('limit', limit)}
          />
        )}
      </div>
    </AdminLayout>
  );
};
